import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Table, Card, Button, Space, Typography, Tag, Badge, Modal,
  notification, Breadcrumb, Alert, Input, Form, Descriptions,
  Select, Divider, List, Popconfirm, Tooltip, Tabs, message, Avatar, Radio
} from 'antd';
import {
  FaTools, FaSyncAlt, FaClock, FaExclamationTriangle,
  FaCheckCircle, FaTimes, FaUserCog, FaInfoCircle, FaWrench, 
  FaUserSlash, FaSearch, FaEdit, FaPlus, FaUserPlus, FaKey, FaSync 
} from 'react-icons/fa';
import './MaintenanceDashboard.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const MAINTENANCE_TASK_STATUS = {
  Pending: 'Pending',
  InProgress: 'InProgress',
  Completed: 'Completed',
  Cancelled: 'Cancelled'
};

const MaintenanceDashboard = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assignForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0
  });
  const [roomMaintenanceStatus, setRoomMaintenanceStatus] = useState({});

  const [activeTab, setActiveTab] = useState('all');
  const [rooms, setRooms] = useState([]);

  const breadcrumbItems = [
    {
      title: <Link to={authData?.role?.toLowerCase() === 'systemadmin' ? '/admin/dashboard' : '/hotels/dashboard'}>Home</Link>,
    },
    {
      title: 'Maintenance Dashboard',
    },
  ];

  const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange', icon: <FaClock /> },
    { value: 'InProgress', label: 'In Progress', color: 'blue', icon: <FaTools /> },
    { value: 'Completed', label: 'Completed', color: 'green', icon: <FaCheckCircle /> },
    { value: 'Cancelled', label: 'Cancelled', color: 'red', icon: <FaTimes /> },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low', color: 'green' },
    { value: 'Medium', label: 'Medium', color: 'orange' },
    { value: 'High', label: 'High', color: 'red' },
    { value: 'Emergency', label: 'Emergency', color: 'volcano' },
  ];

  const checkRoomMaintenance = async (roomNumber) => {
    if (!roomNumber) return false;
    
    try {
      const response = await axiosInstance.get(
        `/Maintenance/CheckRoomMaintenance`, 
        {
          params: { 
            roomNumber, 
            hotelId: authData.hotelId 
          }
        }
      );
      return response.data.isUnderMaintenance;
    } catch (error) {
      console.error('Error checking room status:', error);
      return false;
    }
  };

  useEffect(() => {
    if (authData?.hotelId) {
      fetchTasks();
      fetchMaintenanceStaff();
      fetchRooms();
    }
  }, [authData?.hotelId, filterStatus]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/Maintenance/GetAllMaintenanceTasks?hotelId=${authData.hotelId}`);
      let filteredTasks = response.data || [];
      
      if (filterStatus !== 'All') {
        filteredTasks = filteredTasks.filter(task => task.status === filterStatus);
      }

      const tasksWithKeys = filteredTasks.map(task => ({
        ...task,
        key: task.maintenanceTaskId,
      }));

      setTasks(tasksWithKeys);
      setFilteredTasks(tasksWithKeys);
      updateStats(tasksWithKeys);
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors(err.response?.data);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStaff = async () => {
    try {
      const response = await axiosInstance.get("/User/WithPermission", { 
        params: { 
          hotelId: authData.hotelId,
          permission: "MaintenanceAssignment" 
        } 
      });
      setStaff(response.data.users || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load maintenance staff"
      });
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/Rooms/all/${authData.hotelId}`);
      setRooms(response.data || []);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load rooms"
      });
    }
  };

  const updateStats = useCallback((tasks) => {
    const stats = {
      pending: tasks.filter(t => t.status === MAINTENANCE_TASK_STATUS.Pending).length,
      inProgress: tasks.filter(t => t.status === MAINTENANCE_TASK_STATUS.InProgress).length,
      completed: tasks.filter(t => t.status === MAINTENANCE_TASK_STATUS.Completed).length,
      cancelled: tasks.filter(t => t.status === MAINTENANCE_TASK_STATUS.Cancelled).length
    };
    setStats(stats);
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = tasks.filter(task => 
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (task.roomNumber && task.roomNumber.toString().includes(searchQuery)) ||
        (task.location && task.location.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredTasks(filtered);
      updateStats(filtered);
    } else {
      setFilteredTasks(tasks);
      updateStats(tasks);
    }
  }, [searchQuery, tasks, updateStats]);

  const handleAssignTask = async (values) => {
    try {
      await axiosInstance.post('/Maintenance/Assign', {
        ...values,
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: staff.find(user => user.userId === values.userId),
              assignedToUserId: values.userId,
              assignmentNotes: values.assignmentNotes
            }
          : task
      );
      
      setTasks(updatedTasks);
      setFilteredTasks(updatedTasks);
      notification.success({
        message: 'Success',
        description: 'Task assigned successfully'
      });
      setAssignModalOpen(false);
    } catch (err) {
      console.error("Error assigning task:", err);
      setApiErrors(err.response?.data);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to assign task"
      });
    }
  };

  const handleUnassignTask = async () => {
    try {
      await axiosInstance.post('/Maintenance/Unassign', {
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: null,
              assignedToUserId: null,
              assignmentNotes: null
            }
          : task
      );
      
      setTasks(updatedTasks);
      setFilteredTasks(updatedTasks);
      notification.success({
        message: 'Success',
        description: 'Task unassigned successfully'
      });
      setUnassignModalOpen(false);
    } catch (err) {
      console.error("Error unassigning task:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to unassign task"
      });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      
      // Prepare form data (convert enums if needed)
      const formData = {
        ...values,
        status: values.status ? values.status : undefined, // Already in correct format
        priority: values.priority || undefined
      };

      // Make the API call
      const response = await axiosInstance.put(
        `/Maintenance/${currentTask.maintenanceTaskId}`,
        formData,
        {
          params: { hotelId: authData.hotelId },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? { ...task, ...response.data } // Use data from server response
          : task
      );
      
      setTasks(updatedTasks);
      setFilteredTasks(updatedTasks);
      
      notification.success({
        message: 'Success',
        description: 'Task updated successfully'
      });
      setEditModalOpen(false);
    } catch (err) {
      console.error("Error updating task:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to update task"
      });
    }
  };

  const handleCreateTask = async () => {
    try {
      const values = await createForm.validateFields();
      
      // Validate room maintenance status
      if (values.roomNumber) {
        const isUnderMaintenance = await checkRoomMaintenance(values.roomNumber);
        if (isUnderMaintenance) {
          notification.error({
            message: 'Room Unavailable',
            description: 'This room is already under maintenance. Please select another room or location.',
          });
          return;
        }
      }

      const taskData = {
        title: values.title,
        description: values.description,
        roomNumber: values.roomNumber || null,
        location: values.location || null,
        priority: values.priority,
        reportedBy: authData.firstName || authData.email,
        hotelId: authData.hotelId,
        requestedByUserId: authData.userId
      };

      // Remove null values
      Object.keys(taskData).forEach(key => {
        if (taskData[key] === null || taskData[key] === undefined) {
          delete taskData[key];
        }
      });

      setLoading(true);
      const response = await axiosInstance.post('/Maintenance/CreateTask', taskData);
      
      const newTask = {
        ...response.data,
        key: response.data.maintenanceTaskId,
      };
      
      setTasks(prev => [...prev, newTask]);
      setFilteredTasks(prev => [...prev, newTask]);
      
      notification.success({
        message: 'Task Created',
        description: 'Maintenance task has been successfully created',
      });
      
      setCreateModalOpen(false);
      setRoomMaintenanceStatus({});
      createForm.resetFields();
    } catch (error) {
      console.error("Error creating task:", error);
      
      let errorMessage = "Failed to create task";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = Object.values(error.response.data.errors)
          .flat()
          .join(', ');
      }
      
      notification.error({
        message: 'Creation Failed',
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (values) => {
    try {
      const response = await axiosInstance.post('/Maintenance/CreateTask', values);
      
      const newTask = {
        ...response.data,
        key: response.data.maintenanceTaskId,
      };
      
      setTasks(prev => [...prev, newTask]);
      setFilteredTasks(prev => [...prev, newTask]);
      notification.success({
        message: 'Success',
        description: 'Task created successfully',
      });
      setCreateModalOpen(false);
      createForm.resetFields();
    } catch (err) {
      console.error("Error creating task:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to create task",
      });
      throw err; // Re-throw the error to be caught by handleCreateTask
    }
  };

  const renderStatusTag = (status) => {
    const config = statusOptions.find(opt => opt.value === status) || {};
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label || status}
      </Tag>
    );
  };

  const renderPriorityTag = (priority) => {
    const config = priorityOptions.find(opt => opt.value === priority) || {};
    return (
      <Tag color={config.color}>
        {config.label || priority}
      </Tag>
    );
  };

  const handleDeleteTask = async (taskId) => {
    try {
      // First get the task details to check if it has a roomId
      const taskToDelete = tasks.find(t => t.maintenanceTaskId === taskId);
      const hasRoom = taskToDelete?.roomId;

      // Delete the task
      const response = await axiosInstance.delete(
        `/Maintenance/DeleteTask/${taskId}`,
        {
          params: { 
            hotelId: authData.hotelId,
            timestamp: Date.now() // Cache buster
          },
          validateStatus: (status) => 
            status === 204 || status === 404 // Accept both success and not found
        }
      );

      if (response.status === 204) {
        // Success case - update room status if needed
        if (hasRoom) {
          try {
            await axiosInstance.put(`/Rooms/UpdateStatus/${taskToDelete.roomId}`, {
              status: 'Available', // Or whatever default status you want
              hotelId: authData.hotelId
            });
          } catch (roomError) {
            console.error("Error updating room status:", roomError);
            // Don't fail the whole operation if room update fails
            notification.warning({
              message: 'Task Deleted',
              description: 'Task was deleted but room status could not be updated',
            });
          }
        }

        // Update local state
        setTasks(tasks.filter(t => t.maintenanceTaskId !== taskId));
        notification.success({
          message: 'Task Deleted',
          description: 'The maintenance task was successfully removed',
        });
      } else {
        // 404 case
        notification.warning({
          message: 'Task Not Found',
          description: response.data?.message || 'The task may have already been deleted',
        });
        fetchTasks(); // Refresh list
      }
    } catch (err) {
      console.error("Delete error:", err);
      notification.error({
        message: 'Deletion Failed',
        description: err.response?.data?.message || 'Could not delete the task',
      });
    }
  };

  const columns = [
    {
      title: 'Task ID',
      dataIndex: 'maintenanceTaskId',
      key: 'maintenanceTaskId',
      width: 100,
      sorter: (a, b) => a.maintenanceTaskId - b.maintenanceTaskId,
      render: (id, record) => (
        <Button type="link" onClick={() => {
          setCurrentTask(record);
          setInfoModalOpen(true);
        }}>
          {id}
        </Button>
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Button type="link" onClick={() => {
          setCurrentTask(record);
          setInfoModalOpen(true);
        }}>
          {text}
        </Button>
      )
    },
    {
      title: 'Room/Location',
      key: 'location',
      render: (_, record) => record.roomNumber || record.location || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: statusOptions.map(opt => ({ text: opt.label, value: opt.value })),
      render: renderStatusTag,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: renderPriorityTag
    },
    {
      title: 'Assigned To',
      key: 'assignedTo',
      render: (_, record) => {
        if (record.assignedTo) {
          return (
            <div>
              <Text>{record.assignedTo.userName}</Text>
              <br />
              <Text type="secondary">{record.assignedTo.role}</Text>
            </div>
          );
        }
        return <Text type="secondary">Not assigned</Text>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          {/* Edit Button */}
          <Tooltip title="Edit Task">
            <Button 
              icon={<FaEdit />}
              onClick={() => {
                setCurrentTask(record);
                editForm.setFieldsValue({
                  title: record.title,
                  description: record.description,
                  status: record.status,
                  priority: record.priority,
                  notes: record.notes
                });
                setEditModalOpen(true);
              }}
            />
          </Tooltip>

          {/* Assign/Unassign Button */}
          {!record.assignedTo ? (
            <Tooltip title="Assign Task">
              <Button
                icon={<FaUserCog />}
                onClick={() => {
                  setCurrentTask(record);
                  setAssignModalOpen(true);
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Unassign Task">
              <Button
                icon={<FaUserSlash />}
                danger
                onClick={() => {
                  setCurrentTask(record);
                  setUnassignModalOpen(true);
                }}
              />
            </Tooltip>
          )}

          {/* Delete Button */}
          <Tooltip title="Delete Task">
            <Popconfirm
              title="Are you sure to delete this task?"
              onConfirm={() => handleDeleteTask(record.maintenanceTaskId)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                icon={<FaTimes />} 
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div className="maintenance-dashboard">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <FaTools style={{ fontSize: '18px', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>Maintenance Dashboard</Title>
            <Badge count={tasks.length} showZero style={{ backgroundColor: '#1890ff' }} />
            <Button 
              type="text" 
              icon={<FaInfoCircle />} 
              onClick={() => setInfoModalOpen(true)}
              tooltip="Dashboard Information"
            />
          </Space>
        }
        extra={
          <Space>
            <Input
              placeholder="Search tasks..."
              prefix={<FaSearch />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              value={filterStatus}
              style={{ width: 150 }}
              onChange={setFilterStatus}
              options={[
                { value: 'All', label: 'All Statuses' },
                ...statusOptions.map(opt => ({ value: opt.value, label: opt.label }))
              ]}
            />
            <Button 
              type="primary"
              icon={<FaPlus />}
              onClick={() => setCreateModalOpen(true)}
            >
              Create Task
            </Button>
            <Button 
              icon={<FaSyncAlt />} 
              onClick={fetchTasks} 
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {apiErrors && (
          <Alert
            message="Error"
            description={apiErrors.message || 'An error occurred'}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            onClose={() => setApiErrors(null)}
          />
        )}

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 16 }}>
          <Card 
            hoverable 
            onClick={() => setFilterStatus('All')}
            style={filterStatus === 'All' ? { border: '2px solid #1890ff' } : {}}
          >
            <Title level={4}>All Tasks</Title>
            <Title level={2}>{tasks.length}</Title>
          </Card>
          <Card 
            hoverable 
            onClick={() => setFilterStatus(MAINTENANCE_TASK_STATUS.Pending)}
            style={filterStatus === MAINTENANCE_TASK_STATUS.Pending ? { border: '2px solid #1890ff' } : {}}
          >
            <Title level={4}>Pending</Title>
            <Title level={2}>{stats.pending}</Title>
          </Card>
          <Card 
            hoverable 
            onClick={() => setFilterStatus(MAINTENANCE_TASK_STATUS.InProgress)}
            style={filterStatus === MAINTENANCE_TASK_STATUS.InProgress ? { border: '2px solid #1890ff' } : {}}
          >
            <Title level={4}>In Progress</Title>
            <Title level={2}>{stats.inProgress}</Title>
          </Card>
          <Card 
            hoverable 
            onClick={() => setFilterStatus(MAINTENANCE_TASK_STATUS.Completed)}
            style={filterStatus === MAINTENANCE_TASK_STATUS.Completed ? { border: '2px solid #1890ff' } : {}}
          >
            <Title level={4}>Completed</Title>
            <Title level={2}>{stats.completed}</Title>
          </Card>
          <Card 
            hoverable 
            onClick={() => setFilterStatus(MAINTENANCE_TASK_STATUS.Cancelled)}
            style={filterStatus === MAINTENANCE_TASK_STATUS.Cancelled ? { border: '2px solid #1890ff' } : {}}
          >
            <Title level={4}>Cancelled</Title>
            <Title level={2}>{stats.cancelled}</Title>
          </Card>
        </div>

        <Table
          columns={columns}
          dataSource={filteredTasks}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} tasks`
          }}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Space direction="vertical">
                <Text>No maintenance tasks found</Text>
                <Button 
                  icon={<FaSyncAlt  />}
                  onClick={fetchTasks}
                >
                  Refresh
                </Button>
              </Space>
            )
          }}
        />

        {/* Assign Task Modal */}
        <Modal
          title={`Assign Task #${currentTask?.maintenanceTaskId || ''}`}
          open={assignModalOpen}
          onCancel={() => setAssignModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={assignForm}
            layout="vertical"
            onFinish={handleAssignTask}
            initialValues={{
              userId: currentTask?.assignedToUserId || null,
              assignmentNotes: currentTask?.assignmentNotes || ''
            }}
          >
            <Form.Item
              label="Assign To"
              name="userId"
              rules={[{ required: true, message: 'Please select a staff member' }]}
            >
              <Select 
                placeholder={staff.length === 0 ? "No staff available" : "Select maintenance staff"}
                disabled={staff.length === 0}
              >
                {staff
                  // Filter duplicates if needed
                  .filter((user, index, self) => 
                    index === self.findIndex(u => u.userId === user.userId)
                  )
                  // Render options
                  .map(user => (
                    <Option 
                      key={user.userId} // or use composite key if needed
                      value={user.userId}
                    >
                      {user.userName} ({user.role})
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Assignment Notes"
              name="assignmentNotes"
            >
              <TextArea rows={3} placeholder="Any special instructions..." />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Assign Task
                </Button>
                <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Unassign Task Modal */}
        <Modal
          title={`Unassign Task #${currentTask?.maintenanceTaskId || ''}`}
          open={unassignModalOpen}
          onCancel={() => setUnassignModalOpen(false)}
          onOk={handleUnassignTask}
          okText="Confirm Unassign"
          cancelText="Cancel"
        >
          <p>Are you sure you want to unassign this task from {currentTask?.assignedTo?.userName || 'the current assignee'}?</p>
          {currentTask?.assignmentNotes && (
            <Alert 
              message="Assignment Notes" 
              description={currentTask.assignmentNotes} 
              type="info" 
              showIcon
            />
          )}
        </Modal>

        {/* Edit Task Modal */}
        <Modal
          title={`Edit Task #${currentTask?.maintenanceTaskId || ''}`}
          open={editModalOpen}
          onCancel={() => {
            editForm.resetFields();
            setEditModalOpen(false);
          }}
          onOk={handleEditSubmit}
          confirmLoading={loading}
          width={700}
          destroyOnClose
        >
          {currentTask && (
            <Form
              form={editForm}
              layout="vertical"
              initialValues={{
                title: currentTask.title,
                description: currentTask.description,
                status: currentTask.status,
                priority: currentTask.priority,
                notes: currentTask.notes || ''
              }}
            >
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: 'Please enter a title' },
                  { max: 100, message: 'Title cannot exceed 100 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter task title"
                  showCount 
                  maxLength={100}
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { max: 500, message: 'Description cannot exceed 500 characters' }
                ]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Detailed description of the maintenance task"
                  showCount
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select
                  placeholder="Select task status"
                  optionFilterProp="children"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {statusOptions.map(option => (
                    <Option 
                      key={option.value} 
                      value={option.value}
                    >
                      <Space>
                        {option.icon}
                        {option.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select a priority' }]}
              >
                <Select
                  placeholder="Select priority level"
                  optionLabelProp="label"
                >
                  {priorityOptions.map(option => (
                    <Option 
                      key={option.value} 
                      value={option.value}
                      label={
                        <Tag color={option.color}>
                          {option.label}
                        </Tag>
                      }
                    >
                      <Tag color={option.color}>
                        {option.label}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="notes"
                label="Internal Notes"
                tooltip="Visible only to maintenance staff and administrators"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Additional notes or instructions"
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              {/* Display room/location information */}
              {(currentTask.roomNumber || currentTask.location) && (
                <Alert
                  message="Location"
                  description={
                    <Space>
                      {currentTask.roomNumber && (
                        <Tag color="blue">Room: {currentTask.roomNumber}</Tag>
                      )}
                      {currentTask.location && (
                        <Tag color="geekblue">Area: {currentTask.location}</Tag>
                      )}
                    </Space>
                  }
                  type="info"
                  showIcon
                />
              )}

              {/* Display assigned staff if applicable */}
              {currentTask.assignedTo && (
                <Alert
                  message="Assigned To"
                  description={
                    <Space>
                      <Avatar 
                        src={currentTask.assignedTo.avatar} 
                        size="small"
                      >
                        {currentTask.assignedTo.userName.charAt(0)}
                      </Avatar>
                      <Text>
                        {currentTask.assignedTo.userName} ({currentTask.assignedTo.role})
                      </Text>
                    </Space>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </Form>
          )}
        </Modal>

        {/* Create Task Modal */}
        <Modal
          title="Create New Maintenance Task"
          open={createModalOpen}
          onCancel={() => {
            setCreateModalOpen(false);
            setRoomMaintenanceStatus({});
            createForm.resetFields();
          }}
          onOk={() => {
            createForm.validateFields()
              .then(values => {
                const taskData = {
                  title: values.title,
                  description: values.description,
                  priority: values.priority,
                  hotelId: authData.hotelId,
                  requestedByUserId: authData.userId,
                  reportedBy: authData.userName,
                  location: values.roomNumber ? `Room ${values.roomNumber}` : values.location,
                  roomNumber: values.roomNumber || null,
                  roomId: null, // You may need to set this if you have room IDs
                  notes: values.notes || '',
                  dueDate: values.dueDate // Add due date field if needed
                };
                return handleCreateTask(taskData);
              })
              .then(() => {
                message.success('Task created successfully');
                setCreateModalOpen(false);
                createForm.resetFields();
              })
              .catch(error => {
                if (!error.errorFields) {
                  message.error(error.response?.data?.message || 'Failed to create task');
                }
              });
          }}
          confirmLoading={loading}
          width={700}
          destroyOnClose
        >
          <Form
            form={createForm}
            layout="vertical"
            initialValues={{
              priority: 'Medium'
            }}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={[
                { required: true, message: 'Please enter a title' },
                { min: 3, message: 'Title needs at least 3 characters' },
                { max: 100, message: 'Title cannot exceed 100 characters' }
              ]}
            >
              <Input 
                placeholder="Task title" 
                showCount 
                maxLength={100}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true, message: 'Please enter a description' },
                { min: 10, message: 'Description needs at least 10 characters' },
                { max: 500, message: 'Description cannot exceed 500 characters' }
              ]}
            >
              <TextArea 
                rows={4} 
                placeholder="Detailed description of the maintenance task"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item
              name="roomNumber"
              label="Room Number"
              help={roomMaintenanceStatus[createForm.getFieldValue('roomNumber')] 
                ? 'This room is already under maintenance' 
                : null
              }
              validateStatus={roomMaintenanceStatus[createForm.getFieldValue('roomNumber')] 
                ? 'error' 
                : null
              }
            >
              <Input 
                placeholder="Enter room number (optional)"
                onChange={async (e) => {
                  const roomNumber = e.target.value;
                  if (roomNumber) {
                    const isUnderMaintenance = await checkRoomMaintenance(roomNumber);
                    setRoomMaintenanceStatus(prev => ({
                      ...prev,
                      [roomNumber]: isUnderMaintenance
                    }));
                    // Automatically set location when room number entered
                    createForm.setFieldsValue({ 
                      location: `Room ${roomNumber}` 
                    });
                  } else {
                    setRoomMaintenanceStatus({});
                    // Clear location if room number is cleared
                    if (createForm.getFieldValue('location')?.startsWith('Room ')) {
                      createForm.setFieldsValue({ location: '' });
                    }
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="location"
              label="Location"
              rules={[
                { required: true, message: 'Please enter a location' },
                { min: 3, message: 'Location needs at least 3 characters' }
              ]}
            >
              <Input 
                placeholder="Enter location (e.g., 'Lobby', 'Pool Area')"
                onChange={(e) => {
                  // Clear room number if location is manually modified
                  if (!e.target.value.startsWith('Room ')) {
                    createForm.setFieldsValue({ roomNumber: '' });
                    setRoomMaintenanceStatus({});
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: 'Please select a priority' }]}
            >
              <Select
                placeholder="Select priority level"
                optionLabelProp="label"
              >
                {priorityOptions.map(option => (
                  <Option 
                    key={option.value} 
                    value={option.value}
                    label={
                      <Tag color={option.color}>
                        {option.label}
                      </Tag>
                    }
                  >
                    <Tag color={option.color}>
                      {option.label}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="notes"
              label="Internal Notes"
              tooltip="Visible only to maintenance staff and administrators"
            >
              <TextArea 
                rows={3} 
                placeholder="Additional notes or instructions"
                showCount
                maxLength={1000}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Info Modal */}
        <Modal
          title="Maintenance Dashboard Information"
          open={infoModalOpen}
          onCancel={() => setInfoModalOpen(false)}
          footer={null}
          width={800}
        >
          <div className="info-modal-content">
            <p>This module allows you to track and manage all maintenance tasks in your hotel.</p>
            
            <Divider orientation="left">Key Features</Divider>
            <ul>
              <li>Create, view, and manage maintenance tasks</li>
              <li>Assign tasks to maintenance staff</li>
              <li>Track task status and priority</li>
              <li>Filter and search tasks</li>
              <li>View detailed task statistics</li>
            </ul>

            <Divider orientation="left">Staff Assignment Requirements</Divider>
            <Alert
              type="info"
              message="To assign maintenance tasks, staff must have either:"
              description={
                <ul>
                  <li>The <Text strong>"Maintenance"</Text> role assigned to their account, OR</li>
                  <li>The <Text strong>"MaintenanceAssignment"</Text> permission granted</li>
                </ul>
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Alert
              type="warning"
              message="Troubleshooting No Staff Available"
              description={
                <div>
                  <p>If you don't see any staff available for assignment:</p>
                  <ol>
                    <li>Verify users have the Maintenance role or permission</li>
                    <li>Check they are assigned to this hotel location</li>
                    <li>Ensure their accounts are active (not disabled)</li>
                  </ol>
                  <Button 
                    type="primary" 
                    size="small"
                    icon={<FaUserPlus />}
                    onClick={() => {
                      navigate('/hotels/PermissionManager'); // UPDATED PATH
                      setInfoModalOpen(false);
                    }}
                  >
                    Manage Permissions
                  </Button>
                </div>
              }
              showIcon
            />

            <Divider orientation="left">Status Indicators</Divider>
            <List
              size="small"
              dataSource={statusOptions}
              renderItem={item => (
                <List.Item>
                  <Tag color={item.color} icon={item.icon}>
                    {item.label}
                  </Tag>
                  <span style={{ marginLeft: 8 }}>
                    {item.value === 'Pending' && 'Task has been created but not started'}
                    {item.value === 'InProgress' && 'Task is currently being worked on'}
                    {item.value === 'Completed' && 'Task has been finished'}
                    {item.value === 'Cancelled' && 'Task was cancelled'}
                  </span>
                </List.Item>
              )}
            />

            <Divider orientation="left">Priority Levels</Divider>
            <List
              size="small"
              dataSource={priorityOptions}
              renderItem={item => (
                <List.Item>
                  <Tag color={item.color}>
                    {item.label}
                  </Tag>
                  <span style={{ marginLeft: 8 }}>
                    {item.value === 'Low' && 'Minor issues that don\'t affect operations'}
                    {item.value === 'Medium' && 'Issues that should be addressed soon'}
                    {item.value === 'High' && 'Critical issues affecting functionality'}
                    {item.value === 'Emergency' && 'Immediate attention required'}
                  </span>
                </List.Item>
              )}
            />

            <Divider orientation="left">Required Permissions</Divider>
            <Alert
              type="info"
              message="The following permissions must be configured for full functionality:"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Table
              size="small"
              pagination={false}
              dataSource={[
                { key: '1', permission: 'Maintenance.View', description: 'View maintenance tasks and dashboard' },
                { key: '2', permission: 'Maintenance.Create', description: 'Create new maintenance tasks' },
                { key: '3', permission: 'Maintenance.Edit', description: 'Modify existing tasks' },
                { key: '4', permission: 'Maintenance.Assign', description: 'Assign tasks to staff' },
                { key: '5', permission: 'Maintenance.Unassign', description: 'Unassign tasks from staff' },
              ]}
              columns={[
                {
                  title: 'Permission',
                  dataIndex: 'permission',
                  render: (text) => <Text code copyable>{text}</Text>,
                  width: 200
                },
                {
                  title: 'Description',
                  dataIndex: 'description'
                }
              ]}
            />
            
            <Space style={{ marginTop: 16 }}>
              <Button 
                type="primary" 
                icon={<FaKey />}
                onClick={() => {
                  navigate('/hotels/PermissionManager'); // UPDATED PATH
                  setInfoModalOpen(false);
                }}
              >
                Permission Manager
              </Button>
              <Button 
                type="default" 
                icon={<FaUserCog />}
                onClick={() => {
                  navigate('/hotels/PermissionManager'); // UPDATED PATH
                  setInfoModalOpen(false);
                }}
              >
                Role Management
              </Button>
            </Space>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default MaintenanceDashboard;